import React, { useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  Space,
  Table,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTags } from "hooks";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import RichTextEditor from "components/common/RichTextEditor";
import IconButton from "common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const toTagName = (tag) => String(tag?.name || tag?.title || tag?.label || "Tag");

const TagsPage = () => {
  const { tags, loading, createTag, updateTag, deleteTag, fetchTagById } =
    useTags(0, 500);

  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [activeTag, setActiveTag] = useState(null);
  const [detailsTag, setDetailsTag] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const filteredTags = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return tags;
    return tags.filter((tag) => {
      const name = toTagName(tag).toLowerCase();
      const id = String(tag?.id || "").toLowerCase();
      const description = String(tag?.description || "").toLowerCase();
      return name.includes(term) || id.includes(term) || description.includes(term);
    });
  }, [query, tags]);


  const openCreate = () => {
    setDrawerMode("create");
    setActiveTag(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (tag) => {
    setDrawerMode("edit");
    setActiveTag(tag);
    form.setFieldsValue({ name: toTagName(tag), description: tag?.description || "" });
    setDrawerOpen(true);
  };

  const openDetails = async (tag) => {
    setDetailsLoading(true);
    try {
      const fullTag = await fetchTagById(tag.id);
      setDetailsTag(fullTag || tag);
    } catch (error) {
      setDetailsTag(tag);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActiveTag(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    const name = String(values.name || "").trim();
    const description = values.description ? String(values.description).trim() : "";
    if (!name) return;

    try {
      if (drawerMode === "edit" && activeTag?.id != null) {
        await updateTag(activeTag.id, { name, description });
      } else {
        await createTag({ name, description });
      }
      closeDrawer();
    } catch (error) {
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    await deleteTag(deleteTarget.id);
    if (detailsTag?.id === deleteTarget.id) {
      setDetailsTag(null);
    }
    setDeleteTarget(null);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value, record) => {
        const name = toTagName(record);
        return (
          <div style={{ fontWeight: 600, color: "#111827" }}>{name}</div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc) => <div style={{ color: "#475569" }}>{desc || "No description"}</div>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "right",
      render: (_, record) => {
        const items = [
          { key: "view", icon: <EyeOutlined />, label: "View details" },
          { key: "edit", icon: <EditOutlined />, label: "Edit tag" },
          { key: "delete", icon: <DeleteOutlined />, label: "Delete", danger: true },
        ];

        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation?.();
                if (key === "edit") openEdit(record);
                if (key === "view") openDetails(record);
                if (key === "delete") setDeleteTarget(record);
              },
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="tags"
          title="Project Management Tags"
          subtitle="Create and organize tags to keep projects and tasks consistent and easy to filter"
          className="flex-1"
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            placeholder="Search tags"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 260, borderRadius: 12 }}
          />
        </Space>

        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          className="text-xs text-white px-3 fw-normal"
          style={{ height: 32 }}
          onClick={openCreate}
        >
          New tag
        </IconButton>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredTags}
          bordered
          size="small"
          tableLayout="fixed"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            columnWidth: 36,
            renderCell: (_, __, ___, originNode) =>
              React.cloneElement(originNode, {
                style: { transform: "scale(0.85)", transformOrigin: "center" },
              }),
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e?.target?.closest?.(".ant-checkbox-wrapper")) return;
              if (e?.target?.closest?.(".ant-dropdown")) return;
              openDetails(record);
            },
            style: { cursor: "pointer" },
          })}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
          scroll={{ x: 860 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No tags found"
                style={{ padding: "40px 0" }}
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={drawerMode === "edit" ? "Edit Tag" : "Create Tag"}
        placement="right"
        width={460}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnHidden
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            label="Tag name"
            name="name"
            rules={[
              { required: true, whitespace: true, message: "Please enter a tag name" },
            ]}
            extra="Use a short, recognizable label like Frontend or Security."
          >
            <Input placeholder="e.g. Frontend" maxLength={60} autoFocus />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: false }]}
          >
            <RichTextEditor placeholder="Optional description (shown in table)" height="180px" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {drawerMode === "edit" ? "Save changes" : "Create tag"}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Drawer
        title="Tag details"
        placement="right"
        width={420}
        open={Boolean(detailsTag)}
        onClose={() => setDetailsTag(null)}
        destroyOnHidden
      >
        {detailsLoading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
            Loading tag...
          </div>
        ) : detailsTag ? (
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div
              style={{
                padding: 16,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fafafa",
              }}
            >
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                Tag name
              </Typography.Text>
              <Typography.Title level={4} style={{ margin: "4px 0 0" }}>
                {toTagName(detailsTag)}
              </Typography.Title>
            </div>

            <div>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                Description
              </Typography.Text>
              <Typography.Paragraph style={{ margin: "6px 0 0", color: "#334155" }}>
                {detailsTag?.description || "No description provided."}
              </Typography.Paragraph>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 8 }}>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  const tag = detailsTag;
                  setDetailsTag(null);
                  openEdit(tag);
                }}
              >
                Edit
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteTarget(detailsTag)}>
                Delete
              </Button>
            </div>
          </Space>
        ) : null}
      </Drawer>

      <ConfirmDeleteModal
        show={Boolean(deleteTarget)}
        setShow={(show) => {
          if (!show) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
        loading={loading}
        itemName="tag"
        message={`Delete ${toTagName(deleteTarget)}? This action will remove it from the tag list.`}
      />
    </div>
  );
};

export default TagsPage;
